import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { captureFlowError, captureFlowStep } from "@/core/monitoring/sentry";
import { AuthEntryCard } from "@/features/auth/components/AuthEntryCard";
import { RegistrationBackButton } from "@/features/auth/components/RegistrationBackButton";
import { AuthField } from "@/features/auth/components/AuthField";
import { Button } from "@/shared/components/ui/Button";
import { registrationService } from "@/features/auth/services/registration.service";
import {
  getRegistrationState,
  setRegistrationStarted,
} from "@/features/auth/store/registration.store";

const PHONE_PREFIXES: Record<string, string> = {
  EC: "+593 ",
  PE: "+51 ",
  US: "+1 ",
};

const PHONE_DIGIT_LENGTHS: Record<string, number> = {
  "+593 ": 9,
  "+51 ": 9,
  "+1 ": 10,
};

function normalizeEmail(value: string) {
  return value.trimStart();
}

function normalizePhone(value: string, prefix: string): string {
  let raw = value;
  const prefixDigits = prefix.trim();
  if (raw.startsWith(prefixDigits)) raw = raw.slice(prefixDigits.length);
  const digits = raw
    .replace(/\D/g, "")
    .slice(0, PHONE_DIGIT_LENGTHS[prefix] ?? 9);

  if (!digits) return prefix;

  const parts: string[] = [];
  const groups = prefix === "+1 " ? [3, 3, 4] : [3, 3, 3];
  let offset = 0;
  for (const groupLength of groups) {
    if (offset >= digits.length) break;
    parts.push(digits.slice(offset, offset + groupLength));
    offset += groupLength;
  }
  return prefix + parts.join(" ");
}

function isEmailValid(value: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

function isPhoneValid(value: string, prefix: string) {
  if (!value.startsWith(prefix)) return false;
  const digits = value.slice(prefix.length).replace(/\D/g, "");
  return digits.length === (PHONE_DIGIT_LENGTHS[prefix] ?? 9);
}

export function AuthOtpContactPage() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation(["register", "validation"]);
  const phonePrefix =
    PHONE_PREFIXES[getRegistrationState().country ?? "EC"] ?? "+593 ";
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  const isEmailReady = useMemo(() => isEmailValid(email), [email]);
  const isPhoneReady = useMemo(
    () => isPhoneValid(phone, phonePrefix),
    [phone, phonePrefix],
  );
  const canSubmit = isEmailReady && isPhoneReady && !isLoading;
  const emailFormatError =
    email.length > 0 && !isEmailReady ? t("validation:email.invalid") : null;

  async function handleSubmit() {
    if (!canSubmit) return;

    setIsLoading(true);
    setErrorMessage(null);

    const { correlationId } = getRegistrationState();

    if (!correlationId) {
      setIsLoading(false);
      navigate("/", { replace: true });
      return;
    }

    captureFlowStep("registration", "otp-contact-submitted");

    let captureResult;
    try {
      captureResult = await registrationService.captureContact(
        correlationId,
        { email: email.trim(), phoneNumber: phone.replace(/\s/g, "") },
        i18n.language,
      );
    } catch (err) {
      captureFlowError("registration", "otp-contact-submitted", err);
      setIsLoading(false);
      return;
    }

    setIsLoading(false);

    if (!captureResult.success) {
      captureFlowStep("registration", "otp-contact-failed", {
        errorCode: captureResult.errorCode ?? "unknown",
      });
      if (captureResult.errorCode === "ALREADY_REGISTERED") {
        navigate("/login");
        return;
      }
      if (captureResult.errorCode === "PROCESS_BLOCKED") {
        navigate("/register/consent");
        return;
      }
      setErrorMessage(captureResult.message);
      return;
    }

    captureFlowStep("registration", "otp-sent");
    setRegistrationStarted(
      correlationId,
      phone,
      email.trim(),
      captureResult.data.expiresInSeconds ?? 300,
    );
    setShowConfirmationModal(true);
  }

  return (
    <>
      <AuthEntryCard variant="otp-contact">
        <section className="mt-4 w-full max-w-96 text-center">
          <p className="text-sm leading-6 text-text-muted">
            {t("register:otp.description")}
          </p>
        </section>

        <div className="mt-8 flex w-full max-w-96 flex-col gap-5">
          <AuthField
            autoComplete="email"
            inputMode="email"
            label={t("register:otp.email.label")}
            onChange={(event) => {
              setEmail(normalizeEmail(event.target.value));
              setErrorMessage(null);
            }}
            placeholder="usuario@example.com"
            type="email"
            value={email}
          />

          {emailFormatError ? (
            <p className="-mt-2 text-sm leading-6 text-[#e02d3c]" role="alert">
              {emailFormatError}
            </p>
          ) : null}

          <AuthField
            autoComplete="tel"
            inputMode="tel"
            label={t("register:otp.phone.label")}
            onFocus={() => {
              if (!phone) setPhone(phonePrefix);
            }}
            onChange={(event) => {
              setPhone(normalizePhone(event.target.value, phonePrefix));
              setErrorMessage(null);
            }}
            onKeyDown={(event) => {
              if (event.key === "Enter") void handleSubmit();
            }}
            placeholder={`${phonePrefix}999 999 999`}
            type="tel"
            value={phone}
          />

          {errorMessage ? (
            <p className="-mt-2 text-sm leading-6 text-[#e02d3c]" role="alert">
              {errorMessage}
            </p>
          ) : null}
        </div>

        <div className="mt-8 flex w-full max-w-96 items-center justify-center gap-3 px-1">
          <RegistrationBackButton />
          <Button
            className="h-9 w-28 shrink-0 px-5 py-1.5 text-sm disabled:bg-[#f1f1f3] disabled:text-[#8c939b] disabled:opacity-100"
            fullWidth={false}
            disabled={!canSubmit}
            onClick={() => void handleSubmit()}
          >
            {isLoading
              ? t("register:otp.btn.sending")
              : t("register:otp.btn.create")}
          </Button>
        </div>
      </AuthEntryCard>

      {showConfirmationModal ? (
        <div
          className="fixed inset-0 z-40 flex items-center justify-center px-4"
          role="dialog"
          aria-modal="true"
        >
          <div className="absolute inset-0 bg-[rgba(5,20,31,0.55)]" />
          <div className="relative z-10 flex w-full max-w-sm flex-col items-center rounded-md bg-white px-6 py-8 text-center shadow-xl">
            <div
              className="flex h-14 w-14 items-center justify-center rounded-full bg-surface-accent-soft text-2xl text-brand-primary"
              aria-hidden="true"
            >
              ✉
            </div>
            <h2 className="mt-4 text-base font-bold leading-5 text-text-primary">
              {t("register:otp.modal.title")}
            </h2>
            <p className="mt-3 text-sm leading-6 text-text-muted">
              {t("register:otp.modal.description")}
            </p>
            <div className="mt-8 w-full">
              <Button
                className="min-h-9 px-5 py-1.5 text-sm"
                onClick={() => navigate("/register/otp/code", { replace: true })}
              >
                {t("register:otp.modal.btn")}
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
